import { type RefObject, useEffect, useState } from "react";

/**
 * Track an element's content width in CSS pixels.
 *
 * A chart has to lay out against the space it actually has. Sizing the SVG to its own content and
 * letting CSS scale it down looks fine until the content is either very small (one column in a
 * wide panel, rendered as a sliver) or very wide (everything shrunk, including the text, which
 * then no longer matches the font size the labels were measured against).
 */
export function useElementWidth(ref: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (element === null) {
      return;
    }
    const measure = (): void => setWidth(element.clientWidth);

    // Measure once up front. The observer is supposed to deliver an initial callback, but it
    // cannot be relied on -- in one embedding it delivers nothing at all -- and a first paint at
    // width 0 would lay every label out against no space.
    measure();

    // Two sources, because neither covers the other. The window event catches an ordinary
    // resize even where the observer is inert; the observer catches the container changing
    // while the window does not, such as a panel opening beside it.
    window.addEventListener("resize", measure);
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            // Read from the element rather than the entry: contentRect and clientWidth disagree
            // about padding, and every other path here uses clientWidth.
            measure();
          });
    observer?.observe(element);

    return () => {
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, [ref]);

  return width;
}
